import { apiClient } from './apiClient';
import type { BrandBible } from '../types/tenant';

const isNotFoundError = (err: unknown): boolean => {
  return typeof err === 'object'
    && err !== null
    && 'response' in err
    && typeof (err as { response?: { status?: unknown } }).response?.status === 'number'
    && (err as { response?: { status?: number } }).response?.status === 404;
};

/** Response shape from GET /content-calendar/brand-bible */
interface BrandBibleApiResponse {
  id: string;
  tenant_id: string;
  company_name: string;
  description: string;
  services_offered: string[];
  industry?: string;
  target_audience?: string;
  competitors: Array<{ name: string; website: string }>;
  marketing_focus?: string;
  positioning?: string;
  service_locations?: string[];
  locations?: Record<string, string[]>;
  tone_of_writing?: string;
  customer_pain_points?: string;
  credentials_licenses?: string;
  company_mission?: string;
  call_to_action?: string;
  value_proposition?: string;
  website_audience?: string;
  seasonality?: string;
  sales_process?: string;
  promotions_specials?: string;
  market_overview?: string;
  visual_identity?: string;
  social_media_links?: BrandBible['social_media_links'];
  case_studies?: string;
  contact_info?: BrandBible['contact_info'];
  brand_restrictions?: BrandBible['brand_restrictions'];
  buyer_personas?: BrandBible['buyer_personas'];
  theme_config?: BrandBible['theme_config'];
  author_attribution?: BrandBible['author_attribution'];
  primary_colors?: string;
  secondary_colors?: string;
  typography?: string;
  // P1 Phase A — LLM-synthesized keyword research brief from the auto-fill
  // agent; drives the AdCampaignKeywordsPanel's prefilled Company info field.
  keyword_research_brief?: string | null;
  keyword_research_brief_generated_at?: string | null;
  field_review?: BrandBible['field_review'];
  created_at: string;
  updated_at: string;
}

export interface BrandBibleCompanyInfoSynthesisResult {
  tenant_id: string;
  company_information: string;
  sources_used: string[];
}

/** Map backend API response to the BrandBible type used throughout the frontend. */
function mapToBrandBible(r: BrandBibleApiResponse): BrandBible {
  return {
    id: r.id,
    website: r.tenant_id,
    business_name: r.company_name,
    description: r.description,
    services_offered: r.services_offered,
    ideal_client: r.target_audience,
    competitors: r.competitors?.map((c) => c.website) ?? [],
    marketing_focus: r.marketing_focus,
    value_proposition: r.positioning ?? r.value_proposition,
    service_locations: r.service_locations,
    locations: r.locations,
    tone_of_writing: r.tone_of_writing,
    customer_pain_points: r.customer_pain_points,
    credentials_licenses: r.credentials_licenses,
    company_mission: r.company_mission,
    call_to_action: r.call_to_action,
    website_audience: r.website_audience,
    seasonality: r.seasonality,
    sales_process: r.sales_process,
    promotions_specials: r.promotions_specials,
    market_overview: r.market_overview,
    visual_identity: r.visual_identity,
    social_media_links: r.social_media_links,
    case_studies: r.case_studies,
    contact_info: r.contact_info,
    brand_restrictions: r.brand_restrictions,
    buyer_personas: r.buyer_personas,
    theme_config: r.theme_config,
    author_attribution: r.author_attribution,
    primary_colors: r.primary_colors ?? r.theme_config?.colors?.primary,
    secondary_colors: r.secondary_colors ?? r.theme_config?.colors?.secondary,
    typography: r.typography ?? (
      typeof r.theme_config?.typography === 'string'
        ? r.theme_config.typography
        : r.theme_config?.typography?.fontFamily
    ),
    // P1 Phase A — LLM-synthesized brief saved by the auto-fill agent;
    // drives the AdCampaignKeywordsPanel's prefilled Company info field.
    keyword_research_brief: r.keyword_research_brief,
    keyword_research_brief_generated_at: r.keyword_research_brief_generated_at,
    field_review: r.field_review ?? {},
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

/**
 * CORE-K-09: result of the narrow `service_locations` PATCH. `changed` is
 * false when the server-normalised list equals what it already had.
 */
export interface BrandBibleServiceLocationsPatchResult {
  tenant_id: string;
  changed: boolean;
  service_locations: string[];
  added: string[];
  removed: string[];
}

export const brandBibleService = {
  async get(tenantId: string): Promise<BrandBible | null> {
    try {
      const { data } = await apiClient.get<BrandBibleApiResponse>(
        '/content-calendar/brand-bible',
        { params: { tenant_id: tenantId } },
      );
      return mapToBrandBible(data);
    } catch (err: unknown) {
      if (isNotFoundError(err)) return null;
      throw err;
    }
  },

  async createOrUpdate(tenantId: string, bible: BrandBible): Promise<BrandBible> {
    const { data } = await apiClient.put<BrandBibleApiResponse>(
      '/content-calendar/brand-bible',
      {
        company_name: bible.business_name,
        description: bible.description ?? '',
        services_offered: bible.services_offered ?? [],
        industry: bible.industry,
        target_audience: bible.ideal_client,
        competitors: (bible.competitors ?? []).map((c) =>
          typeof c === 'string' ? { name: c, website: c } : c,
        ),
        marketing_focus: bible.marketing_focus,
        positioning: bible.value_proposition,
        service_locations: bible.service_locations,
        company_mission: bible.company_mission,
        tone_of_writing: bible.tone_of_writing,
        call_to_action: bible.call_to_action,
        customer_pain_points: bible.customer_pain_points,
        website_audience: bible.website_audience,
        seasonality: bible.seasonality,
        sales_process: bible.sales_process,
        promotions_specials: bible.promotions_specials,
        market_overview: bible.market_overview,
        credentials_licenses: bible.credentials_licenses,
        case_studies: bible.case_studies,
        visual_identity: bible.visual_identity,
        social_media_links: bible.social_media_links,
        contact_info: bible.contact_info,
        brand_restrictions: bible.brand_restrictions,
        buyer_personas: bible.buyer_personas,
        theme_config: bible.theme_config,
        author_attribution: bible.author_attribution,
      },
      { params: { tenant_id: tenantId } },
    );
    return mapToBrandBible(data);
  },

  async delete(tenantId: string): Promise<void> {
    await apiClient.delete('/content-calendar/brand-bible', {
      params: { tenant_id: tenantId },
    });
  },

  /** Confirm one critical field (elevated agency staff only). `valueAsSeen` is
   *  the value the reviewer looked at; the API rejects with 409 if it no longer
   *  matches the current value. Returns the refreshed Brand Bible. */
  async confirmField(
    tenantId: string,
    fieldKey: string,
    valueAsSeen: unknown,
  ): Promise<BrandBible> {
    const { data } = await apiClient.post<BrandBibleApiResponse>(
      '/content-calendar/brand-bible/confirm-field',
      { tenant_id: tenantId, field_key: fieldKey, value_as_seen: valueAsSeen },
    );
    return mapToBrandBible(data);
  },

  /** Return one critical field to needs-review (elevated agency staff only). */
  async resetField(tenantId: string, fieldKey: string): Promise<BrandBible> {
    const { data } = await apiClient.post<BrandBibleApiResponse>(
      '/content-calendar/brand-bible/reset-field',
      { tenant_id: tenantId, field_key: fieldKey },
    );
    return mapToBrandBible(data);
  },

  /**
   * CORE-K-09: replace ONLY `service_locations` with the full expected new
   * list. The server trims, dedupes case-insensitively, drops blanks and
   * caps entries; the response carries what it actually stored.
   */
  async patchServiceLocations(
    tenantId: string,
    serviceLocations: string[],
  ): Promise<BrandBibleServiceLocationsPatchResult> {
    // tenant_id rides the query string, like the sibling PUT and the
    // middleware's workspace validation expect; the body is the list only.
    const { data } = await apiClient.patch<BrandBibleServiceLocationsPatchResult>(
      '/content-calendar/brand-bible/service-locations',
      { service_locations: serviceLocations },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async synthesizeCompanyInfo(
    tenantId: string,
    websiteUrl?: string,
  ): Promise<BrandBibleCompanyInfoSynthesisResult> {
    const { data } = await apiClient.post<BrandBibleCompanyInfoSynthesisResult>(
      '/content-calendar/brand-bible/synthesize-company-info',
      { tenant_id: tenantId, website_url: websiteUrl },
      { timeout: 60_000 },
    );
    return data;
  },
};

/** CORE-K-09: standalone alias so callers can import the one function. */
export const patchBrandBibleServiceLocations = (
  tenantId: string,
  serviceLocations: string[],
): Promise<BrandBibleServiceLocationsPatchResult> =>
  brandBibleService.patchServiceLocations(tenantId, serviceLocations);
